import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { purchasesApi } from "../../../api/inventory/purchases";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PlusIcon, PencilIcon, ShoppingCartIcon } from "@heroicons/react/24/outline";
import { Button, MenuItem, Select, FormControl, InputLabel, Autocomplete, TextField } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import toast from "react-hot-toast";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { fetchCustomers } from "../../../api/customer";

export const Purchases = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierSearchQuery, setSupplierSearchQuery] = useState("");
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
    statuses: [],
  });
  const [newPurchase, setNewPurchase] = useState({
    supplier_id: "",
    purchase_date: new Date().toISOString().split("T")[0],
    delivery_date: "",
    reference: "",
  });

  useEffect(() => {
    fetchPurchases();
  }, [filters]);

  // Fetch suppliers with search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchSuppliers(supplierSearchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [supplierSearchQuery]);

  const fetchSuppliers = async (query = "") => {
    try {
      setLoadingSuppliers(true);
      const params = {
        supplier: true, // Filter for suppliers using customer_type
        active: true,
      };
      if (query) {
        params.query = query;
      }
      const response = await fetchCustomers(params);
      // fetchCustomers returns response.data which contains { data: [...], count: ... }
      if (response?.data) {
        setSuppliers(Array.isArray(response.data) ? response.data : []);
      } else if (Array.isArray(response)) {
        // Fallback if response is directly an array
        setSuppliers(response);
      } else {
        setSuppliers([]);
      }
    } catch (err) {
      console.error("Error fetching suppliers:", err);
      setSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const params = {};
      
      // Add query search
      if (filters.query) {
        params.query = filters.query;
      }
      
      // Add status filter (can be array or single value)
      if (filters.statuses && filters.statuses.length > 0) {
        params.status = filters.statuses.length === 1 ? filters.statuses[0] : filters.statuses;
      }
      
      const response = await purchasesApi.getPurchases(params);
      if (response?.data?.success) {
        setPurchases(response.data.data);
      } else {
        setError("Kunne ikke hente innkjøp");
      }
    } catch (err) {
      console.error("Error fetching purchases:", err);
      setError("Kunne ikke hente innkjøp");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newPurchase.supplier_id) {
      toast.error("Leverandør er påkrevd");
      return;
    }

    try {
      const response = await purchasesApi.createPurchase({
        supplier_id: parseInt(newPurchase.supplier_id),
        purchase_date: newPurchase.purchase_date,
        delivery_date: newPurchase.delivery_date || null,
        reference: newPurchase.reference || "",
        status: "draft",
      });

      if (response?.data?.success) {
        setCreateDialogOpen(false);
        setNewPurchase({ supplier_id: "", purchase_date: new Date().toISOString().split("T")[0], delivery_date: "", reference: "" });
        setSupplierSearchQuery("");
        fetchPurchases();
        navigate(`/dashboard/inventory/purchases/${response.data.data.id}`);
        toast.success("Innkjøp opprettet");
      } else {
        toast.error("Kunne ikke opprette innkjøp");
      }
    } catch (error) {
      console.error("Error creating purchase:", error);
      toast.error("Kunne ikke opprette innkjøp");
    }
  };

  const getStatusColor = (status) => {
    // Import status from Utils if needed, or use inline mapping
    const statusMap = {
      draft: "bg-gray-100 text-gray-800",
      sent: "bg-blue-100 text-blue-800",
      confirmed: "bg-yellow-100 text-yellow-800",
      received: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return statusMap[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      draft: "Utkast",
      sent: "Sendt",
      confirmed: "Bekreftet",
      received: "Mottatt",
      cancelled: "Kansellert",
    };
    return statusMap[status] || status;
  };

  // Get selected supplier for Autocomplete
  const selectedSupplier = useMemo(() => {
    if (!newPurchase.supplier_id) return null;
    return suppliers.find((s) => s.id === parseInt(newPurchase.supplier_id)) || null;
  }, [newPurchase.supplier_id, suppliers]);

  if (loading) {
    return (
      <>
        <DashboardHeader title="Innkjøp" subtitle="Laster..." />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceLoading />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DashboardHeader title="Innkjøp" subtitle="" />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceError errors={[error]} />
        </div>
      </>
    );
  }

  return (
    <>
      <DashboardHeader
        title="Innkjøp"
        subtitle="Administrer innkjøp fra leverandører"
        actions={[
          {
            component: null,
            label: "Nytt innkjøp",
            description: "",
            path: null,
            action: () => {
              setCreateDialogOpen(true);
              fetchSuppliers(); // Load initial suppliers
            },
            icon: PlusIcon,
            textColor: "text-white",
            bgColor: "bg-blue-600",
            hidden: false,
            menu: true,
          },
        ]}
      />
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8">
          {/* ModuleFilter for search and filtering */}
          <div className="mb-6">
            <ModuleFilter
              onFiltersChange={setFilters}
              initialFilters={filters}
              acceptedStatuses={["draft", "sent", "confirmed", "received", "cancelled"]}
              searchPlaceholder="Søk etter innkjøp..."
              filterTitle="Filtrer på status"
            />
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            {purchases.length > 0 ? (
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Innkjøpsnummer</TableCell>
                    <TableCell>Leverandør</TableCell>
                    <TableCell>Innkjøpsdato</TableCell>
                    <TableCell>Leveringsdato</TableCell>
                    <TableCell>Senddato</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Referanse</TableCell>
                    <TableCell align="right">Handlinger</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchases.map((purchase) => (
                    <TableRow key={purchase.id} hover>
                      <TableCell>{purchase.purchase_number || "-"}</TableCell>
                      <TableCell>{purchase.supplier?.displayTitle || purchase.supplier?.name || "-"}</TableCell>
                      <TableCell>
                        {purchase.purchase_date
                          ? new Date(purchase.purchase_date).toLocaleDateString("nb-NO")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {purchase.delivery_date
                          ? new Date(purchase.delivery_date).toLocaleDateString("nb-NO")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {purchase.send_date
                          ? new Date(purchase.send_date).toLocaleDateString("nb-NO")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            purchase.status
                          )}`}
                        >
                          {getStatusLabel(purchase.status)}
                        </span>
                      </TableCell>
                      <TableCell>{purchase.reference || "-"}</TableCell>
                      <TableCell align="right">
                        <button
                          onClick={() => navigate(`/dashboard/inventory/purchases/${purchase.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Vis"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <ShoppingCartIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Ingen innkjøp</h3>
                <p className="text-gray-500 mb-4">Opprett ditt første innkjøp for å komme i gang</p>
                <Button variant="contained" onClick={() => {
                  setCreateDialogOpen(true);
                  fetchSuppliers();
                }}>
                  Opprett innkjøp
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Dialog */}
      <Transition.Root show={createDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setCreateDialogOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                      Opprett nytt innkjøp
                    </Dialog.Title>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Leverandør *</label>
                        <Autocomplete
                          options={suppliers}
                          getOptionLabel={(option) => option.displayTitle || option.name || ""}
                          value={selectedSupplier}
                          onChange={(event, newValue) => {
                            setNewPurchase({
                              ...newPurchase,
                              supplier_id: newValue ? newValue.id.toString() : "",
                            });
                          }}
                          onInputChange={(event, newInputValue) => {
                            setSupplierSearchQuery(newInputValue);
                          }}
                          loading={loadingSuppliers}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Søk etter leverandør..."
                              variant="outlined"
                              fullWidth
                            />
                          )}
                          noOptionsText="Ingen leverandører funnet"
                          loadingText="Laster leverandører..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Innkjøpsdato *</label>
                        <TextField
                          fullWidth
                          type="date"
                          variant="outlined"
                          value={newPurchase.purchase_date}
                          onChange={(e) => setNewPurchase({ ...newPurchase, purchase_date: e.target.value })}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Leveringsdato</label>
                        <TextField
                          fullWidth
                          type="date"
                          variant="outlined"
                          value={newPurchase.delivery_date}
                          onChange={(e) => setNewPurchase({ ...newPurchase, delivery_date: e.target.value })}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Referanse</label>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={newPurchase.reference}
                          onChange={(e) => setNewPurchase({ ...newPurchase, reference: e.target.value })}
                          placeholder="Referanse"
                        />
                      </div>

                      <div className="flex justify-end space-x-2 mt-4">
                        <Button variant="outlined" onClick={() => {
                          setCreateDialogOpen(false);
                          setSupplierSearchQuery("");
                        }}>
                          Avbryt
                        </Button>
                        <Button variant="contained" onClick={handleCreate}>
                          Opprett
                        </Button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};
